/**
 * ItemOrders — vendor-side "Item Orders" tab inside the Orders page.
 * Lists Shopify-style marketplace item orders with inline fulfillment
 * (mark shipped w/ tracking, mark delivered).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Package, Truck, CheckCircle2, ChevronDown, Store } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { GlassCard, API } from './GlassCard';

const statusCls = {
  pending:   'bg-yellow-500/15 text-[#FCD34D] border-yellow-500/20',
  paid:      'bg-blue-500/15 text-[#93C5FD] border-blue-500/20',
  shipped:   'bg-purple-500/15 text-[#C4B5FD] border-purple-500/20',
  delivered: 'bg-green-500/15 text-[#86EFAC] border-green-500/20',
  completed: 'bg-green-500/15 text-[#86EFAC] border-green-500/20',
  cancelled: 'bg-red-500/15 text-[#FCA5A5] border-red-500/20',
  failed:    'bg-red-500/15 text-[#FCA5A5] border-red-500/20',
};

function abs(url) {
  if (!url) return '';
  return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

export default function ItemOrders() {
  const { getAuthHeaders } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [tracking, setTracking] = useState({ number: '', url: '' });

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/item-orders/mine`, { headers: getAuthHeaders() });
      if (r.ok) setOrders(await r.json());
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const markShipped = async (orderId) => {
    try {
      const r = await fetch(`${API}/item-orders/${orderId}/mark-shipped`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracking_number: tracking.number, tracking_url: tracking.url }),
      });
      if (!r.ok) throw new Error('failed');
      toast.success('Order marked shipped');
      setTracking({ number: '', url: '' });
      fetchOrders();
    } catch {
      toast.error('Could not mark shipped');
    }
  };

  const markDelivered = async (orderId) => {
    try {
      const r = await fetch(`${API}/item-orders/${orderId}/mark-delivered`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error('failed');
      toast.success('Order marked delivered');
      fetchOrders();
    } catch {
      toast.error('Could not mark delivered');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#8B5CF6]" /></div>;
  }

  if (orders.length === 0) {
    return (
      <GlassCard className="text-center py-12 px-6" data-testid="item-orders-empty">
        <Package size={40} className="mx-auto mb-4 text-[#9CA3AF] opacity-40" />
        <p className="text-[#9CA3AF]">No item orders yet</p>
        <p className="text-[#9CA3AF]/60 text-xs mt-1">
          Marketplace item orders will land here once a buyer completes checkout.
        </p>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-3" data-testid="item-orders-list">
      {orders.map((o) => {
        const cls = statusCls[o.status] || statusCls.pending;
        const isOpen = expanded === o.order_id;
        return (
          <GlassCard key={o.order_id} className="overflow-hidden" data-testid={`item-order-${o.order_id}`}>
            <button
              onClick={() => setExpanded(isOpen ? null : o.order_id)}
              className="w-full p-5 flex items-center justify-between hover:bg-white/5 transition-all text-left"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6]/40 to-[#EC4899]/40 flex items-center justify-center">
                  <Store size={16} className="text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm truncate">
                    {o.lines?.length || 0} item{(o.lines?.length || 0) === 1 ? '' : 's'} · {o.buyer_name || o.buyer_email}
                  </p>
                  <p className="text-[#64748B] text-xs">
                    #{o.order_id.slice(-8)} · {o.created_at ? new Date(o.created_at).toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-[#A78BFA] font-bold">${Number(o.grand_total || 0).toFixed(2)}</span>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${cls}`}>
                  {o.status || 'pending'}
                </span>
                <ChevronDown size={16} className={`text-[#9CA3AF] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-white/5 p-5 space-y-5">
                {/* Lines */}
                <div className="space-y-3">
                  {(o.lines || []).map((l, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#1A1D4A] border border-white/5 flex-shrink-0">
                        {l.image ? <img src={abs(l.image)} alt="" className="w-full h-full object-cover" /> : <Package className="w-full h-full p-3 text-[#9CA3AF]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-semibold truncate">{l.title}</p>
                        {l.variant_label && <p className="text-xs text-[#9CA3AF]">{l.variant_label}</p>}
                        <p className="text-xs text-[#9CA3AF]">Qty {l.quantity}</p>
                      </div>
                      <p className="text-sm font-semibold text-white">${Number(l.line_total).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                {/* Shipping details */}
                <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-sm space-y-1">
                  <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold mb-2">Shipping to</p>
                  <p className="text-white">{o.shipping_address?.line1}</p>
                  {o.shipping_address?.line2 && <p className="text-white">{o.shipping_address.line2}</p>}
                  <p className="text-white">
                    {o.shipping_address?.city}, {o.shipping_address?.state} {o.shipping_address?.zip} · {o.shipping_address?.country}
                  </p>
                  <p className="text-[#9CA3AF] text-xs mt-2">
                    {o.shipping_carrier?.toUpperCase() || 'STD'} · {o.shipping_service || 'Standard shipping'} · ${Number(o.shipping_total || 0).toFixed(2)}
                  </p>
                  {o.tracking_number && (
                    <p className="text-xs mt-1">
                      <span className="text-[#9CA3AF]">Tracking:</span>{' '}
                      {o.tracking_url
                        ? <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[#C4B5FD] hover:text-white">{o.tracking_number}</a>
                        : <span className="text-white">{o.tracking_number}</span>}
                    </p>
                  )}
                </div>

                {/* Fulfillment controls */}
                {o.status === 'paid' && (
                  <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3" data-testid={`item-order-ship-form-${o.order_id}`}>
                    <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">Mark as shipped</p>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        value={tracking.number}
                        onChange={(e) => setTracking({ ...tracking, number: e.target.value })}
                        placeholder="Tracking number (optional)"
                        className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#8B5CF6]"
                      />
                      <input
                        value={tracking.url}
                        onChange={(e) => setTracking({ ...tracking, url: e.target.value })}
                        placeholder="Tracking URL (optional)"
                        className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#8B5CF6]"
                      />
                    </div>
                    <button
                      onClick={() => markShipped(o.order_id)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white hover:shadow-[0_6px_20px_rgba(139,92,246,0.4)]"
                      data-testid={`item-order-mark-shipped-${o.order_id}`}
                    >
                      <Truck size={13} /> Mark shipped
                    </button>
                  </div>
                )}

                {o.status === 'shipped' && (
                  <button
                    onClick={() => markDelivered(o.order_id)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-emerald-500/20 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/30"
                    data-testid={`item-order-mark-delivered-${o.order_id}`}
                  >
                    <CheckCircle2 size={13} /> Mark delivered
                  </button>
                )}
              </div>
            )}
          </GlassCard>
        );
      })}
    </div>
  );
}
